import { useState, useEffect, useRef } from "react";
import { Search, ShoppingCart } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { ProductCard, ShoppingCart as Cart } from "../components/Products";
import { CartItem, Product } from "../types/product.types";
import { RootState } from "../redux/store";
import {
  fetchProducts,
  setLastFetchedPage,
  setPage,
  setSearch,
} from "../redux/slices/productSlice";
import { LoaderCard } from "../components/Products/ProductLoaderCard";
import toast from "react-hot-toast";
import { setLoginModalState } from "../redux/slices/popupModalSlices";
import { orderProducts } from "../services/productOrder.service";
import { createOrder, verifyPayment } from "../services/payment.service";

export default function Products() {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const [params, setParams] = useSearchParams();
  const urlSearch = params.get("search") || "";

  const [cart, setCart] = useState<CartItem[]>([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const {
    items,
    page,
    loading,
    hasMore,
    search,
    nextPageCount,
    lastFetechedPage,
  } = useSelector((state: RootState) => state.products);
  const auth = useSelector((state: RootState) => state.auth);
  const { selectedCity } = useSelector((state: RootState) => state.city);

  useEffect(() => {
    urlSearch && dispatch(setSearch(urlSearch));
  }, [urlSearch, dispatch]);

  useEffect(() => {
    if (page <= lastFetechedPage) return;
    dispatch(fetchProducts({ page, search: urlSearch }) as any);
    dispatch(setLastFetchedPage(page));
  }, [page, urlSearch, lastFetechedPage, dispatch]);

  useEffect(() => {
    if (loading) return;

    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && hasMore) {
        dispatch(setPage(page + 1));
      }
    });

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => observer.disconnect();
  }, [loading, hasMore, page, dispatch]);

  const addToCart = (product: Product) => {
    if (!auth?.token || !auth?.user?.name) {
      dispatch(setLoginModalState(true));
      return;
    }

    toast.success("Product added successfully!");

    setCart((prev) => {
      const index = prev.findIndex((p) => p._id === product._id);

      if (index !== -1) {
        return prev.map((item, i) =>
          i === index ? { ...item, quantity: item.quantity + 1 } : item,
        );
      }

      return [...prev, { ...product, quantity: 1 }];
    });
  };

  const removeFromCart = (id: string) => {
    setCart((prev) => {
      const item = prev.find((p) => p._id === id);
      if (!item) return prev;

      if (item.quantity > 1) {
        return prev.map((p) =>
          p._id === id ? { ...p, quantity: p.quantity - 1 } : p,
        );
      }

      return prev.filter((p) => p._id !== id);
    });
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    dispatch(setSearch(value));
    dispatch(setPage(1));

    if (!value.trim()) {
      params.delete("search");
      setParams(params, { replace: true });
    } else {
      setParams({ search: value }, { replace: true });
    }
  };

  const handleProductPaymentSuccess = async (
    response: any,
    addressId: string,
  ) => {
    try {
      const { data: verifyData, error: verifyError } =
        await verifyPayment(response);

      if (verifyError || !verifyData?.success) {
        toast.error(t("Payment verification failed"));
        return;
      }

      for (const item of cart) {
        const { error } = await orderProducts(
          auth.user!._id,
          item._id,
          selectedCity._id,
          selectedCity.city,
          item.quantity.toString(),
          (item.price * item.quantity).toString(),
          addressId,
        );

        if (error) {
          toast.error(`Failed to order ${item.name}`);
          return;
        }
      }

      setCart([]);
      setIsCartOpen(false);
      toast.success(t("Order placed successfully!"));
    } catch (err) {
      console.error(err);
      toast.error(t("Something went wrong"));
    }
  };

  const handleProductOrder = async (addressId: string) => {
    const totalPrice = cart
      .reduce((total, item) => total + item.price * (item.quantity || 1), 0)
      .toFixed(2);

    const { data, error } = await createOrder(Number(totalPrice));

    if (error || !data) {
      toast.error(t("Unable to initiate payment"));
      return;
    }

    const options = {
      key: data.key,
      order_id: data.orderId,
      amount: data.amount,
      currency: "INR",
      name: "Autocare",

      handler: (response: any) =>
        handleProductPaymentSuccess(response, addressId),

      modal: {
        ondismiss: () => {
          toast.error(t("Payment cancelled"));
        },
      },

      theme: {
        color: localStorage.getItem("theme") === "dark" ? "#facc15" : "#3b82f6",
      },
    };

    const rzp = new (window as any).Razorpay(options);
    rzp.open();
  };

  return (
    <div className="min-h-screen w-full animate-fade-in-up bg-primaryBg dark:bg-primaryBgDark px-6 py-10 custom-scroll">
      <div className="relative max-w-lg mx-auto mb-10">
        <Search className="absolute left-3 top-2.5 text-gray-400 w-5 h-5" />
        <input
          type="text"
          placeholder={t("searchProducts") || "Search products ..."}
          value={search}
          onChange={handleSearchChange}
          className="w-full pl-10 pr-4 py-2 rounded-md dark:border-gray-700 dark:bg-gray-800 text-primaryText dark:text-primaryTextDark focus:outline-none focus:ring-2 focus:ring-secondaryText dark:focus:ring-secondaryTextDark"
        />
      </div>
      {items?.length > 0 ? (
        <div className="grid gap-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
          {items.map((item) => (
            <ProductCard
              key={item._id}
              product={item}
              onAddToCart={() => addToCart(item)}
            />
          ))}

          {loading &&
            hasMore &&
            Array.from({ length: nextPageCount }).map((_, i) => (
              <LoaderCard key={`loader-${i}`} />
            ))}
        </div>
      ) : (
        <div className="grid gap-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
          {Array.from({ length: nextPageCount }).map((_, i) => (
            <LoaderCard key={`loader-${i}`} />
          ))}
        </div>
      )}

      {items?.length > 0 && (
        <div ref={loadMoreRef} className="h-10 mt-1 w-full"></div>
      )}

      {isCartOpen ? (
        <Cart
          cart={cart}
          removeFromCart={removeFromCart}
          setIsCartOpen={setIsCartOpen}
          handleProductOrder={handleProductOrder}
        />
      ) : (
        <button
          onClick={() => setIsCartOpen((prev) => !prev)}
          className="fixed bottom-6 right-6 z-50 bg-secondaryText dark:bg-secondaryTextDark text-primaryText p-4 rounded-full shadow-2xl flex items-center justify-center"
        >
          <ShoppingCart className="w-6 h-6" />
          {cart.length > 0 && (
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold w-5 h-5 flex items-center justify-center rounded-full">
              {cart.reduce((total, item) => total + (item.quantity || 1), 0)}
            </span>
          )}
        </button>
      )}
    </div>
  );
}
